<script setup>
  import { ref, onMounted, onUnmounted } from 'vue';

  const showScrollTop = ref(false);
  const showHoverText = ref(false);
  let hoverTimer = null;

  const hideHoverText = () => {
    showHoverText.value = false;
    if (hoverTimer) clearTimeout(hoverTimer);
  };

  const handleScroll = () => {
    // Show button after scrolling down 300px
    showScrollTop.value = window.scrollY > 300;

    // Reset hover text and timer on scroll
    hideHoverText();
  };

  const scrollToTop = () => {
    hideHoverText();

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startHoverTextTimer = () => {
    // Start timer to show hover text
    hoverTimer = setTimeout(() => {
      showHoverText.value = true;
    }, 100); // Show text after 0.1s delay
  };

  const cancelHoverTextTimer = () => {
    // Clear timer and hide hover text
    hideHoverText();
  };

  onMounted(() => {
    window.addEventListener('scroll', handleScroll);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);

    // Clear hover timer on unmount
    hideHoverText();
  });
</script>

<template>
  <!-- Scroll to Top Button -->
    <Transition name="slide-fade">
      <button
        v-show="showScrollTop"
        @click="scrollToTop"
        @mouseenter="startHoverTextTimer"
        @mouseleave="cancelHoverTextTimer"
        class="scroll-top"
        :class="{ 'show-text': showHoverText }"
        aria-label="Back to top"
      >
        <span class="scroll-top-icon">▲</span>
        <span class="scroll-top-text">Back to Top</span>
      </button>
    </Transition>
</template>

<style scoped>
  /* Scroll to Top Button */
  .scroll-top {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    color: #ffaa00; /* Orange arrow */
    border: 2px solid #ffaa00;
    background: rgba(255, 170, 0, 0.1);
    cursor: pointer;
    border-radius: 50%;
    opacity: 0.7;
    z-index: 999;
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .scroll-top:hover {
    opacity: 1;
    transform: scale(1.2);
  }
  .scroll-top-icon {
    display: inline-block;
    transition: all 0.3s ease-in-out;
  }

  .scroll-top:hover .scroll-top-icon {
    transform: translateY(-7px);
  }

  .scroll-top-text {
    position: absolute;
    right: 100%;
    margin-right: 10px;
    white-space: nowrap;
    opacity: 0;
    transform: translateX(10px);
    transition: all 0.3s ease-in-out;
    font-size: 14px;
    font-weight: 500;
    color: #ffaa00;
    pointer-events: none;
  }

  /* Hover effect for scroll-top text */
  .scroll-top.show-text .scroll-top-text {
    opacity: 1;
    transform: translateX(0);
  }

  /* Slide in effect for scroll-top button */
  .slide-fade-enter-active {
    transition: all 0.3s ease;
  }

  .slide-fade-leave-active {
    transition: all 0.3s ease;
    pointer-events: none;
  }

  .slide-fade-enter-from{
    opacity: 0;
    transform: translateY(30px);
  }

  .slide-fade-enter-to {
    opacity: 0.7;
    transform: translateY(0);
  }

  .slide-fade-leave-from {
    opacity: 0.7;
    transform: translateY(0);
  }

  .slide-fade-leave-to {
    opacity: 0;
    transform: translateY(30px);
  }
</style>